import React, { useState, useEffect, useMemo, useCallback } from "react";
import { ChevronDownIcon, ChevronRightIcon, MapPinIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useIsMobile } from "@/hooks/use-mobile";
import { GeographicNode, GeographicHierarchy } from "@/types";

export interface GeographicSelectorProps {
  geographicHierarchy: GeographicHierarchy | null;
  onChange?: (node: GeographicNode) => void;
  className?: string;
  pillStyle?: "default" | "primary" | "custom";
  pillClassName?: string;
  customIcon?: React.ReactNode;
  isLoading?: boolean;
  loadingError?: string | null;
  value?: GeographicNode | null;
  renderInlineTree?: boolean;
}

const isLeafNode = (node: GeographicNode): boolean => {
  return !node?.trees || node.trees.length === 0;
};

const getRootNode = (
  hierarchy: GeographicHierarchy | null,
): GeographicNode | null => {
  if (!hierarchy?.trees?.length) return null;
  
  // If there's only one root tree, use it directly
  if (hierarchy.trees.length === 1) {
    return hierarchy.trees[0];
  }

  // If there are multiple root trees, create a virtual organization-level root
  return {
    id: "org-root",
    name: hierarchy.hierarchy_name,
    slug: hierarchy.hierarchy_slug,
    resolution: "Organization",
    selectable: true,
    trees: hierarchy.trees,
  } as GeographicNode;
};

export function GeographicSelector({
  geographicHierarchy,
  onChange,
  className,
  pillStyle = "primary",
  pillClassName,
  customIcon,
  isLoading,
  loadingError,
  value,
  renderInlineTree = false,
}: GeographicSelectorProps) {
  const rootNode = useMemo(
    () => getRootNode(geographicHierarchy),
    [geographicHierarchy],
  );
  const [selectedNode, setSelectedNode] = useState<GeographicNode | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const isMobile = useIsMobile();
  const shouldRenderInlineTree = renderInlineTree || isMobile;

  const toggleExpanded = useCallback((id: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Sync with controlled value when provided externally (e.g. map double-click drill-down)
  useEffect(() => {
    if (value !== undefined && value !== null) {
      setSelectedNode(value);
    }
  }, [value]);

  // Set initial selection when hierarchy loads
  useEffect(() => {
    if (geographicHierarchy && !selectedNode && value === undefined) {
      const root = getRootNode(geographicHierarchy);
      if (root) {
        setSelectedNode(root);
        onChange?.(root);
      }
    }
  }, [geographicHierarchy, selectedNode, onChange, value]);

  const handleSelect = (node: GeographicNode) => {
    setSelectedNode(node);
    onChange?.(node);
    // Close the dropdown after selection
    setDropdownOpen(false);
  };

  // Auto-expand the ancestor path to the selected node when the inline-tree
  // menu opens so the user re-enters at their current location instead of a
  // fully-collapsed tree.
  useEffect(() => {
    if (!shouldRenderInlineTree || !dropdownOpen || !selectedNode || !rootNode) {
      return;
    }
    const path = new Set<string>();
    const findPath = (node: GeographicNode): boolean => {
      if (node.id === selectedNode.id) return true;
      if (!node.trees?.length) return false;
      for (const child of node.trees) {
        if (findPath(child)) {
          path.add(node.id);
          return true;
        }
      }
      return false;
    };
    findPath(rootNode);
    if (path.size > 0) {
      setExpandedNodes((prev) => {
        const next = new Set(prev);
        path.forEach((id) => next.add(id));
        return next;
      });
    }
  }, [shouldRenderInlineTree, dropdownOpen, selectedNode, rootNode]);

  const getPillClasses = () => {
    const baseClasses =
      "px-2 py-1 rounded-md text-xs font-medium flex items-center cursor-pointer transition-colors";
    if (pillClassName) return cn(baseClasses, pillClassName);

    switch (pillStyle) {
      case "primary":
        return cn(
          baseClasses,
          "bg-primary/10 text-primary hover:bg-primary/20",
        );
      case "default":
        return cn(
          baseClasses,
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        );
      default:
        return baseClasses;
    }
  };

  /**
   * Render a resolution label from the first child at a level
   */
  const renderResolutionLabel = (
    children: GeographicNode[],
  ): React.ReactNode => {
    if (!children.length) return null;
    const resolution = children[0].resolution;
    // Skip "Organization" resolution label
    if (!resolution || resolution === "Organization") return null;
    return (
      <>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
          {resolution}
        </DropdownMenuLabel>
      </>
    );
  };

  const isNodeSelectable = (node: GeographicNode): boolean => {
    return node.selectable !== false;
  };

  const isAncestorOfSelected = (node: GeographicNode): boolean => {
    if (!selectedNode || !node.trees?.length) return false;
    return node.trees.some(
      (child) => child.id === selectedNode.id || isAncestorOfSelected(child),
    );
  };

  // Sub-trigger handler used on desktop. Must NOT call preventDefault /
  // stopPropagation: Radix composes handlers via composeEventHandlers, which
  // bails out if event.defaultPrevented is true — calling preventDefault here
  // silently suppresses Radix's own onClick that opens the sub-menu.
  const handleSubTriggerSelect = (node: GeographicNode) => {
    setSelectedNode(node);
    onChange?.(node);
    setDropdownOpen(false);
  };

  /**
   * Recursively render a node and its children.
   * - Flyout mode: uses Radix flyout sub-menus (DropdownMenuSub).
   * - Inline-tree mode: renders an indented tree, since flyout sub-menus
   *   cascade off-screen inside narrow panels (Filters sheet) and don't open
   *   reliably on touch.
   */
  const renderNode = (
    node: GeographicNode,
    depth: number = 0,
  ): React.ReactNode => {
    const isSelected = selectedNode?.id === node.id;
    const hasSelectedDescendant = !isSelected && isAncestorOfSelected(node);
    const hasChildren = !isLeafNode(node);
    const canSelect = isNodeSelectable(node);

    // Leaf node — simple menu item (same on mobile and desktop)
    if (!hasChildren) {
      const indentPx = shouldRenderInlineTree ? depth * 12 : 0;
      return (
        <DropdownMenuItem
          key={node.id}
          onClick={canSelect ? () => handleSelect(node) : undefined}
          disabled={!canSelect}
          style={
            shouldRenderInlineTree ? { paddingLeft: 8 + indentPx } : undefined
          }
          className={cn(
            isSelected && canSelect && "bg-primary/10 text-primary font-medium",
            !canSelect && "opacity-50 cursor-not-allowed text-muted-foreground",
          )}
        >
          {node.name}
        </DropdownMenuItem>
      );
    }

    // Inline indented tree
    if (shouldRenderInlineTree) {
      const isExpanded = expandedNodes.has(node.id);
      const indentPx = depth * 12;
      return (
        <React.Fragment key={node.id}>
          <div
            className={cn(
              "flex items-stretch rounded-sm",
              hasSelectedDescendant && canSelect && "text-primary",
            )}
          >
            <button
              type="button"
              aria-label={isExpanded ? "Collapse" : "Expand"}
              aria-expanded={isExpanded}
              onClick={(e) => {
                e.stopPropagation();
                toggleExpanded(node.id);
              }}
              style={{ marginLeft: indentPx }}
              className="flex w-6 flex-shrink-0 items-center justify-center text-muted-foreground hover:text-foreground"
            >
              <ChevronRightIcon
                className={cn(
                  "h-3.5 w-3.5 transition-transform",
                  isExpanded && "rotate-90",
                )}
              />
            </button>
            <button
              type="button"
              onClick={() => {
                if (canSelect) handleSelect(node);
                else toggleExpanded(node.id);
              }}
              disabled={!canSelect && !hasChildren}
              className={cn(
                "flex flex-1 items-center px-1 py-1.5 text-left text-sm rounded-sm hover:bg-accent",
                isSelected &&
                  canSelect &&
                  "bg-primary/10 text-primary font-medium",
                hasSelectedDescendant && canSelect && "font-medium",
                !canSelect && "text-muted-foreground cursor-default",
              )}
            >
              {node.name}
            </button>
          </div>
          {isExpanded && (
            <div className="flex flex-col">
              {renderResolutionLabel(node.trees)}
              {node.trees.map((child) => renderNode(child, depth + 1))}
            </div>
          )}
        </React.Fragment>
      );
    }

    // Desktop: Radix flyout sub-menu
    return (
      <DropdownMenuSub key={node.id}>
        <DropdownMenuSubTrigger
          className={cn(
            isSelected && canSelect && "bg-primary/10 text-primary font-medium",
            hasSelectedDescendant && canSelect && "text-primary font-medium",
            !canSelect && "opacity-50 text-muted-foreground",
          )}
          onClick={canSelect ? () => handleSubTriggerSelect(node) : undefined}
        >
          {node.name}
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="w-56 max-h-[300px] overflow-y-auto">
          {canSelect ? (
            <DropdownMenuItem
              className={cn(
                isSelected && "bg-primary/10 text-primary font-medium",
              )}
              onClick={() => handleSelect(node)}
            >
              Select {node.name}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              disabled
              className="border-b border-border opacity-50 cursor-not-allowed text-muted-foreground"
            >
              Select {node.name}
            </DropdownMenuItem>
          )}
          {renderResolutionLabel(node.trees)}
          {node.trees.map((child) => renderNode(child, depth + 1))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div
        className={cn(getPillClasses(), "opacity-60 cursor-not-allowed")}
        role="status"
        aria-live="polite"
      >
        <span>Loading Geographies ...</span>
      </div>
    );
  }

  // Error state
  if (loadingError) {
    return (
      <div
        className={cn(getPillClasses(), "bg-destructive/10 text-destructive")}
      >
        <span>Error loading data</span>
      </div>
    );
  }

  // No data state
  if (!rootNode || !selectedNode) {
    return (
      <div className={cn(getPillClasses(), "opacity-60 cursor-not-allowed")}>
        {customIcon || (
          <MapPinIcon className="h-3 w-3 mr-1 text-secondary-foreground stroke-current opacity-70" />
        )}
        <span>No territories available</span>
      </div>
    );
  }

  return (
    <div className={className}>
      <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={getPillClasses()}
            aria-label={`Selected geography: ${selectedNode.name}`}
          >
            {customIcon || (
              <MapPinIcon className="h-3 w-3 mr-1 text-secondary-foreground stroke-current opacity-70" aria-hidden />
            )}
            <span>{selectedNode.name}</span>
            <ChevronDownIcon className="h-3 w-3 ml-1 opacity-70" aria-hidden />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="w-60 max-h-[400px] overflow-y-auto"
        >
          {isNodeSelectable(rootNode) ? (
            <DropdownMenuItem
              className={cn(
                "font-medium border-b border-border",
                selectedNode?.id === rootNode.id
                  ? "bg-primary/10 text-primary"
                  : isAncestorOfSelected(rootNode) && "text-primary",
              )}
              onClick={() => handleSelect(rootNode)}
            >
              Select {rootNode.name}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              disabled
              className="font-medium opacity-50 cursor-not-allowed text-muted-foreground"
            >
              Select {rootNode.name}
            </DropdownMenuItem>
          )}
          {/* Resolution label for first level */}
          {renderResolutionLabel(rootNode.trees)}
          {/* Render tree directly */}
          {rootNode.trees.map((child) => renderNode(child, 0))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
